import React, { useState } from "react";
import {
  Modal,
  Input,
  Button,
  Alert,
  Progress,
  Checkbox,
  Typography,
  Spin,
  Tag,
  notification,
} from "antd";
import {
  LinkOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  PictureOutlined,
  EnvironmentOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { useQueryClient } from "react-query";
import {
  scrapeRestaumaticMenu,
  fetchLogoAsBlob,
  ScrapedMenu,
  ScrapedMenuCategory,
} from "../../../service/restaumatic-scraper";
import {
  createProductCategory,
  createProduct,
  createProductVariant,
  editCompanyBranch,
  uploadBranchLogo,
  uploadProductImage,
} from "../../../service";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";
import { colors, spacing } from "../../../styles/theme";

const { Text } = Typography;

const GOOGLE_MAPS_API_KEY = "AIzaSyA3lh6-T9qUYkdo46jFI1jaNQ_z2JNdCEs";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const Section = styled.div`
  margin-bottom: ${spacing.lg};
`;

const CategoryRow = styled.div<{ $checked: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
  padding: ${spacing.sm} 0;
  border-bottom: 1px solid ${colors.border};
  opacity: ${({ $checked }) => ($checked ? 1 : 0.45)};

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryName = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.4;
`;

const ItemList = styled.div`
  margin-top: ${spacing.xs};
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemRow = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  display: flex;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

const BranchInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  padding: ${spacing.md};
  background: ${colors.backgroundLight};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  margin-bottom: ${spacing.md};
`;

const BranchInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const LogoPreview = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid ${colors.border};
  flex-shrink: 0;
`;

const LogoPlaceholder = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 1px solid ${colors.border};
  background: ${colors.backgroundGray};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textSecondary};
  flex-shrink: 0;
`;

const DoneBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.xl} 0;
  text-align: center;
`;

const DoneDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: ${spacing.xs};
`;

const DoneDetail = styled.div`
  font-size: 13px;
  color: ${colors.textSecondary};
`;

// ─── Types ───────────────────────────────────────────────────────────────────

type Step = "input" | "preview" | "importing" | "done";

interface ImportFromUrlModalProps {
  open: boolean;
  onClose: () => void;
}

interface ImportResult {
  categories: number;
  products: number;
  imagesSet: number;
  nameSet: boolean;
  logoSet: boolean;
  addressSet: boolean;
}

// ─── Geocoding ───────────────────────────────────────────────────────────────

async function geocodeAddress(
  address: string
): Promise<{ lat: number; lng: number; formattedAddress: string } | null> {
  const url =
    `https://maps.googleapis.com/maps/api/geocode/json` +
    `?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}&region=pl&language=pl`;

  const res = await fetch(url);
  const data = await res.json();

  if (data.status === "OK" && data.results.length > 0) {
    const result = data.results[0];
    return {
      lat: result.geometry.location.lat,
      lng: result.geometry.location.lng,
      formattedAddress: result.formatted_address,
    };
  }
  return null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ImportFromUrlModal: React.FC<ImportFromUrlModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBranch } = useBranchContext();

  const [step, setStep] = useState<Step>("input");
  const [url, setUrl] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [scrapedData, setScrapedData] = useState<ScrapedMenu | null>(null);

  // Menu selection
  const [selectedIndexes, setSelectedIndexes] = useState<Set<string>>(new Set());

  // Branch settings selection
  const [importName, setImportName] = useState(true);
  const [importLogo, setImportLogo] = useState(true);
  const [importAddress, setImportAddress] = useState(true);
  const [importImages, setImportImages] = useState(true);

  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const handleFetch = async () => {
    if (!url.trim()) return;
    setIsFetching(true);
    setFetchError(null);

    try {
      const data = await scrapeRestaumaticMenu(url);
      setScrapedData(data);
      setSelectedIndexes(new Set(data.categories.map((_, i) => String(i))));
      setImportName(true);
      setImportLogo(!!data.logoUrl);
      setImportAddress(!!data.address);
      const hasAnyImage = data.categories.some((cat) => cat.items.some((item) => item.imageUrl));
      setImportImages(hasAnyImage);
      setStep("preview");
    } catch (err: any) {
      setFetchError(err?.message || t("importFromUrlModal.unknownError"));
    } finally {
      setIsFetching(false);
    }
  };

  // ── Import ─────────────────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!scrapedData) return;

    const toImport: ScrapedMenuCategory[] = scrapedData.categories.filter((_, i) =>
      selectedIndexes.has(String(i))
    );
    const totalProducts = toImport.reduce((sum, cat) => sum + cat.items.length, 0);

    setImportProgress({ current: 0, total: totalProducts });
    setStep("importing");

    let importedProducts = 0;
    let importedCategories = 0;
    let imagesSet = 0;
    let nameSet = false;
    let logoSet = false;
    let addressSet = false;

    try {
      // ── 1. Menu import ──────────────────────────────────────────────────────
      for (const category of toImport) {
        const newCategory = await createProductCategory({ name: category.name });
        importedCategories++;

        for (const item of category.items) {
          const newProduct = await createProduct({
            name: item.name,
            description: item.description || "",
            productCategoryId: newCategory.id,
            isVisible: true,
            temporaryHidden: false,
          });

          if (newProduct?.id && item.priceInGrosze !== null) {
            await createProductVariant({
              productBaseId: newProduct.id,
              name: "Podstawowa",
              price: item.priceInGrosze,
              key: "default",
            });
          }

          // Upload product image if available
          if (importImages && newProduct?.id && item.imageUrl) {
            try {
              const blob = await fetchLogoAsBlob(item.imageUrl);
              await uploadProductImage(newProduct.id, blob);
              imagesSet++;
            } catch {
              // Non-fatal — continue without image
            }
          }

          importedProducts++;
          setImportProgress({ current: importedProducts, total: totalProducts });
        }
      }

      await queryClient.invalidateQueries(["/product-categories"]);

      // ── 2. Logo upload ──────────────────────────────────────────────────────
      if (importLogo && scrapedData.logoUrl && currentBranch?.id) {
        try {
          const blob = await fetchLogoAsBlob(scrapedData.logoUrl);
          await uploadBranchLogo(currentBranch.id, blob);
          await queryClient.invalidateQueries(["/company-branches"]);
          logoSet = true;
        } catch {
          notification.warning({
            message: t("importFromUrlModal.logoSetFailedMessage"),
            description: t("importFromUrlModal.logoSetFailedDescription"),
            placement: "bottomRight",
          });
        }
      }

      // ── 3. Name + address (batched into one PATCH) ─────────────────────────
      if (currentBranch?.id && (importName || importAddress)) {
        try {
          const body: any = {};

          if (importName && scrapedData.restaurantName) {
            body.name = scrapedData.restaurantName;
          }

          if (importAddress && scrapedData.address) {
            const geo = await geocodeAddress(scrapedData.address);
            if (geo) {
              body.geographicPoint = { lat: geo.lat, lng: geo.lng };
              body.formattedAddress = geo.formattedAddress;
            }
          }

          if (Object.keys(body).length > 0) {
            await editCompanyBranch({ id: currentBranch.id, data: body });
            await queryClient.invalidateQueries(["/company-branches"]);
            if (body.name) nameSet = true;
            if (body.formattedAddress) addressSet = true;
          }
        } catch {
          notification.warning({
            message: t("importFromUrlModal.branchUpdateFailedMessage"),
            description: t("importFromUrlModal.branchUpdateFailedDescription"),
            placement: "bottomRight",
          });
        }
      }

      setImportResult({ categories: importedCategories, products: importedProducts, imagesSet, nameSet, logoSet, addressSet });
      setStep("done");
    } catch (err: any) {
      notification.error({
        message: t("importFromUrlModal.importErrorMessage"),
        description: err?.response?.data?.message || err?.message || t("importFromUrlModal.tryAgain"),
        style: { background: colors.errorBackground },
        placement: "bottomRight",
      });
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const toggleCategory = (index: string) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const formatPrice = (priceInGrosze: number | null): string => {
    if (priceInGrosze === null) return "—";
    return `${(priceInGrosze / 100).toFixed(2).replace(".", ",")} ${currencySymbol(currentBranch?.currency)}`;
  };

  const handleClose = () => {
    setStep("input");
    setUrl("");
    setFetchError(null);
    setScrapedData(null);
    setSelectedIndexes(new Set());
    setImportName(true);
    setImportLogo(true);
    setImportAddress(true);
    setImportImages(true);
    setImportProgress({ current: 0, total: 0 });
    setImportResult(null);
    onClose();
  };

  const selectedCount = selectedIndexes.size;
  const selectedProductCount = scrapedData
    ? scrapedData.categories
        .filter((_, i) => selectedIndexes.has(String(i)))
        .reduce((sum, cat) => sum + cat.items.length, 0)
    : 0;

  const progressPercent =
    importProgress.total > 0
      ? Math.round((importProgress.current / importProgress.total) * 100)
      : 0;

  const hasBranchExtras = !!scrapedData;

  // ── Title & footer ─────────────────────────────────────────────────────────

  const titleByStep: Record<Step, string> = {
    input: t("importFromUrlModal.titleInput"),
    preview: t("importFromUrlModal.titlePreview"),
    importing: t("importFromUrlModal.titleImporting"),
    done: t("importFromUrlModal.titleDone"),
  };

  const footerByStep = (): React.ReactNode => {
    if (step === "input") {
      return (
        <Button type="primary" onClick={handleFetch} loading={isFetching} disabled={!url.trim()}>
          {t("importFromUrlModal.fetchMenuButton")}
        </Button>
      );
    }

    if (step === "preview") {
      return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button onClick={() => setStep("input")}>{t("importFromUrlModal.backButton")}</Button>
          <Button type="primary" onClick={handleImport} disabled={selectedCount === 0}>
            {t("importFromUrlModal.importButton", { count: selectedProductCount })}
          </Button>
        </div>
      );
    }

    if (step === "importing") return null;

    return (
      <Button type="primary" onClick={handleClose}>
        {t("importFromUrlModal.closeButton")}
      </Button>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      title={titleByStep[step]}
      open={open}
      onCancel={step === "importing" ? undefined : handleClose}
      closable={step !== "importing"}
      maskClosable={step !== "importing"}
      keyboard={step !== "importing"}
      footer={footerByStep()}
      width={560}
      destroyOnClose={false}
    >
      {/* ── STEP: input ── */}
      {step === "input" && (
        <>
          <Section>
            <Text type="secondary" style={{ display: "block", marginBottom: spacing.md }}>
              {t("importFromUrlModal.inputDescription")}
            </Text>
            <Input
              prefix={<LinkOutlined />}
              placeholder="https://www.restauracja.pl/restauracja/nazwa"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onPressEnter={handleFetch}
              disabled={isFetching}
              size="large"
            />
          </Section>

          {isFetching && (
            <div style={{ textAlign: "center", padding: `${spacing.lg} 0` }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />} />
              <div style={{ marginTop: spacing.sm, color: colors.textSecondary, fontSize: 13 }}>
                {t("importFromUrlModal.fetchingMenu")}
              </div>
            </div>
          )}

          {fetchError && (
            <Alert
              type="error"
              message={t("importFromUrlModal.fetchFailedMessage")}
              description={fetchError}
              showIcon
              style={{ marginTop: spacing.md }}
            />
          )}

          <Alert
            type="info"
            message={t("importFromUrlModal.restaumaticOnlyInfo")}
            showIcon
            style={{ marginTop: spacing.md }}
          />
        </>
      )}

      {/* ── STEP: preview ── */}
      {step === "preview" && scrapedData && (
        <>
          {/* Branch extras: logo + address */}
          {hasBranchExtras && (
            <Section>
              <Text strong style={{ display: "block", marginBottom: spacing.sm }}>
                {t("importFromUrlModal.branchSettingsTitle")}
              </Text>
              <BranchInfoBox>
                <BranchInfoRow>
                  <Checkbox
                    checked={importName}
                    onChange={(e) => setImportName(e.target.checked)}
                  />
                  <div
                    style={{
                      width: 48,
                      display: "flex",
                      justifyContent: "center",
                      color: importName ? colors.primary : colors.textSecondary,
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    <ShopOutlined />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{t("importFromUrlModal.branchNameLabel")}</div>
                    <div style={{ fontSize: 12, color: colors.textSecondary }}>
                      {scrapedData.restaurantName}
                    </div>
                  </div>
                </BranchInfoRow>

                {scrapedData.logoUrl && (
                  <BranchInfoRow>
                    <Checkbox
                      checked={importLogo}
                      onChange={(e) => setImportLogo(e.target.checked)}
                    />
                    {importLogo ? (
                      <LogoPreview src={scrapedData.logoUrl} alt="Logo" />
                    ) : (
                      <LogoPlaceholder>
                        <PictureOutlined />
                      </LogoPlaceholder>
                    )}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{t("importFromUrlModal.branchLogoLabel")}</div>
                      <div style={{ fontSize: 12, color: colors.textSecondary }}>
                        {t("importFromUrlModal.branchLogoDescription")}
                      </div>
                    </div>
                  </BranchInfoRow>
                )}

                {scrapedData.address && (
                  <BranchInfoRow>
                    <Checkbox
                      checked={importAddress}
                      onChange={(e) => setImportAddress(e.target.checked)}
                    />
                    <div
                      style={{
                        width: 48,
                        display: "flex",
                        justifyContent: "center",
                        color: importAddress ? colors.primary : colors.textSecondary,
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      <EnvironmentOutlined />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{t("importFromUrlModal.branchAddressLabel")}</div>
                      <div style={{ fontSize: 12, color: colors.textSecondary }}>
                        {scrapedData.address}
                      </div>
                      <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                        {t("importFromUrlModal.branchAddressDescription")}
                      </div>
                    </div>
                  </BranchInfoRow>
                )}
              </BranchInfoBox>
            </Section>
          )}

          {/* Product images */}
          {scrapedData.categories.some((cat) => cat.items.some((item) => item.imageUrl)) && (
            <Section>
              <BranchInfoBox>
                <BranchInfoRow>
                  <Checkbox
                    checked={importImages}
                    onChange={(e) => setImportImages(e.target.checked)}
                  />
                  <div
                    style={{
                      width: 48,
                      display: "flex",
                      justifyContent: "center",
                      color: importImages ? colors.primary : colors.textSecondary,
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    <PictureOutlined />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{t("importFromUrlModal.productImagesLabel")}</div>
                    <div style={{ fontSize: 12, color: colors.textSecondary }}>
                      {t("importFromUrlModal.productImagesCount", {
                        withImages: scrapedData.categories.reduce((sum, cat) => sum + cat.items.filter((i) => i.imageUrl).length, 0),
                        total: scrapedData.categories.reduce((sum, cat) => sum + cat.items.length, 0),
                      })}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                      {t("importFromUrlModal.productImagesHint")}
                    </div>
                  </div>
                </BranchInfoRow>
              </BranchInfoBox>
            </Section>
          )}

          {/* Menu */}
          <Section>
            <div style={{ marginBottom: spacing.sm }}>
              <Text strong>{scrapedData.restaurantName}</Text>
              <Tag color="blue" style={{ marginLeft: spacing.sm }}>
                {t("importFromUrlModal.categoriesCount", { count: scrapedData.categories.length })}
              </Tag>
              <Tag color="green">
                {t("importFromUrlModal.productsCount", { count: scrapedData.categories.reduce((s, c) => s + c.items.length, 0) })}
              </Tag>
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("importFromUrlModal.selectCategoriesHint")}
            </Text>
          </Section>

          <div
            style={{
              maxHeight: 340,
              overflowY: "auto",
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: `${spacing.sm} ${spacing.md}`,
            }}
          >
            {scrapedData.categories.map((cat, i) => {
              const idx = String(i);
              const isChecked = selectedIndexes.has(idx);
              return (
                <CategoryRow key={i} $checked={isChecked}>
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggleCategory(idx)}
                    style={{ marginTop: 2 }}
                  />
                  <CategoryInfo>
                    <CategoryName>
                      {cat.name}
                      <Tag style={{ marginLeft: spacing.xs, fontWeight: 400 }}>
                        {t("importFromUrlModal.itemsCount", { count: cat.items.length })}
                      </Tag>
                    </CategoryName>
                    <ItemList>
                      {cat.items.slice(0, 4).map((item, j) => (
                        <ItemRow key={j}>
                          <span>{item.name}</span>
                          <span>{formatPrice(item.priceInGrosze)}</span>
                        </ItemRow>
                      ))}
                      {cat.items.length > 4 && (
                        <ItemRow>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {t("importFromUrlModal.moreItems", { count: cat.items.length - 4 })}
                          </Text>
                        </ItemRow>
                      )}
                    </ItemList>
                  </CategoryInfo>
                </CategoryRow>
              );
            })}
          </div>
        </>
      )}

      {/* ── STEP: importing ── */}
      {step === "importing" && (
        <div style={{ padding: `${spacing.lg} 0` }}>
          <div style={{ textAlign: "center", marginBottom: spacing.md }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <div style={{ marginTop: spacing.sm, color: colors.textSecondary }}>
              {t("importFromUrlModal.creatingProducts")}
            </div>
          </div>
          <Progress
            percent={progressPercent}
            status="active"
            format={() => `${importProgress.current} / ${importProgress.total}`}
          />
          <div style={{ textAlign: "center", marginTop: spacing.sm, fontSize: 13, color: colors.textSecondary }}>
            {t("importFromUrlModal.doNotCloseWindow")}
          </div>
        </div>
      )}

      {/* ── STEP: done ── */}
      {step === "done" && importResult && (
        <DoneBox>
          <CheckCircleOutlined style={{ fontSize: 48, color: colors.success }} />
          <div>
            <Text strong style={{ fontSize: 16 }}>
              {t("importFromUrlModal.doneTitle")}
            </Text>
            <DoneDetails>
              <DoneDetail>
                <Trans
                  i18nKey="importFromUrlModal.doneSummary"
                  values={{ products: importResult.products, categories: importResult.categories }}
                  components={{ b: <strong /> }}
                />
              </DoneDetail>
              {importResult.nameSet && (
                <DoneDetail>
                  <CheckCircleOutlined style={{ color: colors.success, marginRight: 4 }} />
                  {t("importFromUrlModal.doneNameSet")}
                </DoneDetail>
              )}
              {importResult.logoSet && (
                <DoneDetail>
                  <CheckCircleOutlined style={{ color: colors.success, marginRight: 4 }} />
                  {t("importFromUrlModal.doneLogoSet")}
                </DoneDetail>
              )}
              {importResult.addressSet && (
                <DoneDetail>
                  <CheckCircleOutlined style={{ color: colors.success, marginRight: 4 }} />
                  {t("importFromUrlModal.doneAddressSet")}
                </DoneDetail>
              )}
              {importResult.imagesSet > 0 && (
                <DoneDetail>
                  <CheckCircleOutlined style={{ color: colors.success, marginRight: 4 }} />
                  <Trans
                    i18nKey="importFromUrlModal.doneImagesSet"
                    values={{ count: importResult.imagesSet }}
                    components={{ b: <strong /> }}
                  />
                </DoneDetail>
              )}
            </DoneDetails>
          </div>
        </DoneBox>
      )}
    </Modal>
  );
};
